import type {
  AuthConfigInput,
  AuthMode,
  MeshRegistrationStatus,
  ProvisionTenantInput,
  ProvisionedTenant,
  TenantAdmin,
  TenantAdminInput,
  TenantAuthSettings,
} from '@/types/control/tenant-provisioning';

import { controlApiRequest } from './control-api-client';

/**
 * Tenant provisioning on control-api's `/api/admin/provisioning` group
 * (HYD-2113). Wire shape is snake_case and is translated here and nowhere
 * else.
 *
 * `redirect_uri` is never sent on any call: control-api derives it from its
 * own public origin and ignores anything in the body.
 */

const BASE = '/api/admin/provisioning/tenants';

function authPath(tenantId: string): string {
  return `${BASE}/${encodeURIComponent(tenantId)}/auth`;
}

type AdminResponse = {
  id?: unknown;
  email?: unknown;
  name?: unknown;
  roles?: unknown;
};

type ProvisionResponse = {
  tenant?: { id?: unknown; name?: unknown; status?: unknown };
  auth?: {
    mode?: unknown;
    provider?: unknown;
    issuer_url?: unknown;
    client_id?: unknown;
    redirect_uri?: unknown;
  };
  admin?: AdminResponse;
  mesh?: { status?: unknown; error?: unknown };
  roles_seeded?: unknown;
};

type AuthSettingsResponse = {
  mode?: unknown;
  provider?: unknown;
  issuer_url?: unknown;
  client_id?: unknown;
  redirect_uri?: unknown;
  scopes?: unknown;
  allowed_domains?: unknown;
  has_client_secret?: unknown;
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asStrings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function trimAll(values: string[]): string[] {
  return values.map((value) => value.trim()).filter((value) => value !== '');
}

/** Anything with an issuer that is not managed is edited as OIDC. */
function asAuthMode(value: unknown): AuthMode {
  return value === 'microsoft_managed' ? value : 'oidc';
}

const MESH_STATUSES: readonly string[] = ['registered', 'failed', 'skipped'];

/**
 * A status the contract does not name is not success. Reporting it as
 * registered would be the silent partial provisioning this ticket exists to
 * stop, so it is surfaced as a failure with the status in the message.
 */
function toMesh(res: ProvisionResponse['mesh']): ProvisionedTenant['mesh'] {
  const status = asString(res?.status);
  if (!MESH_STATUSES.includes(status)) {
    return {
      status: 'failed',
      error: `control-api reported an unexpected mesh status "${status}".`,
    };
  }
  if (status === 'failed') {
    return {
      status,
      error:
        asString(res?.error) || 'Mesh registration failed without a reason.',
    };
  }
  return { status: status as MeshRegistrationStatus, error: null };
}

function toAdmin(res: AdminResponse | undefined): TenantAdmin {
  return {
    id: asString(res?.id),
    email: asString(res?.email),
    name: asString(res?.name),
    roles: asStrings(res?.roles),
  };
}

function toProvisioned(res: ProvisionResponse): ProvisionedTenant {
  return {
    tenant: {
      id: asString(res.tenant?.id),
      name: asString(res.tenant?.name),
      status: asString(res.tenant?.status),
    },
    auth: {
      mode: asAuthMode(res.auth?.mode),
      provider: asString(res.auth?.provider),
      issuerUrl: asString(res.auth?.issuer_url),
      clientId: asString(res.auth?.client_id),
      redirectUri: asString(res.auth?.redirect_uri),
    },
    admin: toAdmin(res.admin),
    mesh: toMesh(res.mesh),
    rolesSeeded: res.roles_seeded === true,
  };
}

function toAuthSettings(res: AuthSettingsResponse): TenantAuthSettings {
  return {
    mode: res.mode === 'none' ? 'none' : asAuthMode(res.mode),
    provider: asString(res.provider),
    issuerUrl: asString(res.issuer_url),
    clientId: asString(res.client_id),
    redirectUri: asString(res.redirect_uri),
    scopes: asStrings(res.scopes),
    allowedDomains: asStrings(res.allowed_domains),
    hasClientSecret: res.has_client_secret === true,
  };
}

/**
 * Blank strings and empty arrays are omitted rather than sent empty, so the
 * server sees only what the user set. Under Microsoft (managed) that leaves
 * just the provider and the flag, which is exactly the contract.
 */
function authConfigToWire(input: AuthConfigInput): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    provider: input.provider,
    use_system_credentials: input.useSystemCredentials,
  };

  const optional: Record<string, string> = {
    client_id: input.clientId,
    client_secret: input.clientSecret,
    issuer_url: input.issuerUrl,
    auth_url: input.authUrl,
    token_url: input.tokenUrl,
    user_info_url: input.userInfoUrl,
  };
  for (const [key, value] of Object.entries(optional)) {
    const trimmed = value.trim();
    if (trimmed !== '') {
      wire[key] = trimmed;
    }
  }

  const scopes = trimAll(input.scopes);
  if (scopes.length > 0) {
    wire.scopes = scopes;
  }
  const domains = trimAll(input.allowedDomains);
  if (domains.length > 0) {
    wire.allowed_domains = domains;
  }

  return wire;
}

/** `admin_sub` is optional and means "link at first sign-in" when absent. */
function optionalSub(admin: TenantAdminInput): { admin_sub?: string } {
  const sub = admin.adminSub.trim();
  return sub === '' ? {} : { admin_sub: sub };
}

export const tenantProvisioningService = {
  /**
   * Create a tenant. The response is the whole truth: control-api awaits the
   * mesh bootstrap, so a `201` with `mesh.status: "failed"` is returned as
   * such rather than thrown, and the page says what happened.
   */
  async create(input: ProvisionTenantInput): Promise<ProvisionedTenant> {
    const res = await controlApiRequest<ProvisionResponse>({
      method: 'POST',
      path: BASE,
      body: {
        name: input.name.trim(),
        auth_config: authConfigToWire(input.authConfig),
        // Trimmed, never lowercased: the server owns address normalization.
        admin_email: input.admin.email.trim(),
        admin_name: input.admin.name.trim(),
        ...optionalSub(input.admin),
      },
    });

    return toProvisioned(res);
  },

  async getAuth(
    tenantId: string,
    signal?: AbortSignal
  ): Promise<TenantAuthSettings> {
    const res = await controlApiRequest<AuthSettingsResponse>({
      path: authPath(tenantId),
      signal,
    });

    return toAuthSettings(res);
  },

  /**
   * An empty `client_secret` is sent as an empty string on purpose: that is
   * the contract's "keep the stored secret". `scopes` and `allowed_domains`
   * are always sent too, so removing every entry clears them rather than
   * leaving the server to guess.
   */
  async updateAuth(
    tenantId: string,
    authConfig: AuthConfigInput
  ): Promise<TenantAuthSettings> {
    const res = await controlApiRequest<AuthSettingsResponse>({
      method: 'PUT',
      path: authPath(tenantId),
      body: {
        ...authConfigToWire(authConfig),
        client_secret: authConfig.clientSecret.trim(),
        scopes: trimAll(authConfig.scopes),
        allowed_domains: trimAll(authConfig.allowedDomains),
      },
    });

    return toAuthSettings(res);
  },

  /** Adds another initial admin with the roles the create path grants. */
  async addAdmin(
    tenantId: string,
    admin: TenantAdminInput
  ): Promise<TenantAdmin> {
    const res = await controlApiRequest<AdminResponse>({
      method: 'POST',
      path: `${BASE}/${encodeURIComponent(tenantId)}/admins`,
      body: {
        email: admin.email.trim(),
        name: admin.name.trim(),
        ...optionalSub(admin),
      },
    });

    return toAdmin(res);
  },
};
